/**
 * The storefront STORE ID (`campaignId`) the session gate resolved from the
 * URL slug.
 *
 * WHICH STORE A /shop/:slug PAGE SHOWS has ONE answer: the slug, as resolved
 * by StorefrontSessionGate. The gate PROVIDES that id under
 * `STOREFRONT_STORE_ID` to every page it wraps, and pages inject it. They
 * never read `localStorage.campaignId` for it: that key is written by the
 * `login` action from the logged-in ACCOUNT's own `campaign_id` -- a legacy of
 * one-store-per-shopper-account -- so for a back-office user or an
 * impersonation it names an unrelated store (getCampaignById 403, prod
 * 2026-10-07). It still answers a different question inside
 * ensureStorefrontSession: "which store is the session I hold for?".
 *
 * The in-memory `held` copy below predates the injected id (#3983). The
 * gate's own entry still writes it, but no page reads it any more; it is kept
 * only for resolveCartCampaignId (router/cartAccount.js), which has no
 * remaining caller in the app.
 *
 * A leaf module on purpose: the gate, the pages and the cart resolver all
 * import it.
 *
 * PUBLISHED, for the chrome ABOVE the gate. The storefront layout (header
 * cart badge) is the gate's parent, so it cannot inject what the gate
 * provides. The gate therefore also publishes the SAME id, keyed by its
 * slug, and withdraws it when it re-runs entry or unmounts -- so the badge
 * counts the store the pages show, and counts nothing outside the gate
 * (a help article rendered in the storefront layout has no store).
 */
import { shallowRef, unref } from "vue";

/**
 * The injection key under which StorefrontSessionGate PROVIDES the store id
 * it resolved from the URL slug to every page it wraps.
 */
export const STOREFRONT_STORE_ID = Symbol("storefrontStoreId");

/**
 * A store id as a non-empty string, or null when it is not resolved.
 *
 * The literal strings "null" and "undefined" (what `setItem` makes of a null
 * `account.campaign_id`) are "not resolved": interpolated into a URL they
 * reach endpoints production answers with a 500.
 */
export function normalizeStoreId(raw) {
  if (raw == null) return null;
  const value = String(raw).trim();
  if (value === "" || value === "null" || value === "undefined") return null;
  return value;
}

let held = null; // { slug, storeId }

/** Remember that store `slug` is store `storeId`. */
export function holdStorefrontStoreId(slug, storeId) {
  if (!slug || storeId == null || storeId === "") return;
  held = { slug: String(slug), storeId };
}

/** The store id held for `slug`, or null. */
export function heldStorefrontStoreId(slug) {
  return held && slug != null && held.slug === String(slug) ? held.storeId : null;
}

// { slug, storeId } of the MOUNTED, READY gate, or null.
const published = shallowRef(null);

/**
 * The gate: store `slug` is ready as store `storeId`.
 *
 * `sessionless` is true when the page was admitted WITHOUT a store session
 * (the emailed order link / order pages of a closed store). Such a page holds
 * no credential the store's reads would accept, so chrome that fetches store
 * content (the category nav) must not run for it -- see gateAdmitsContentFor.
 */
export function publishGateStore(slug, storeId, { sessionless = false } = {}) {
  const id = normalizeStoreId(storeId);
  published.value =
    slug && id != null
      ? { slug: String(slug), storeId, sessionless: sessionless === true }
      : null;
}

/** The gate: no store is ready (re-entry started, or unmounted). */
export function withdrawGateStore() {
  published.value = null;
}

/**
 * The store id the ready gate published for `slug`, or null. Reactive: a
 * computed that calls this re-evaluates when the gate publishes or withdraws.
 */
export function gateStoreFor(slug) {
  const current = published.value;
  return current && slug != null && current.slug === String(slug)
    ? current.storeId
    : null;
}

/**
 * True when the ready gate has admitted the visitor to `slug` WITH a store
 * session, i.e. when store content may be read and shown. This is the one
 * answer chrome above the gate (the category nav) uses for "may I show this
 * store's content?" -- the same state the wrapped pages render from, so the
 * chrome can never be more permissive than the page beneath it. False on the
 * password wall (no gate mounted), while entry runs, on the closed /
 * not-found / unavailable screens, and on sessionless pages. Reactive.
 */
export function gateAdmitsContentFor(slug) {
  const current = published.value;
  return !!(
    current &&
    slug != null &&
    current.slug === String(slug) &&
    !current.sessionless
  );
}

/** Forget the held and published ids (tests). */
export function forgetStorefrontStoreId() {
  held = null;
  published.value = null;
}

/**
 * The `store_id` argument that NAMES store `storeId` on a cart RPC.
 *
 * Carts are keyed by (account, store), so every cart RPC (store_cart,
 * cart_prices, store_cart_count, update_cart_quantity, delete_cart,
 * checkout_quote, create_pending_order, checkout) names the store it is for
 * -- the gate's id on the storefront, the store being ordered for in the
 * back office -- and the server requires it (per-store carts U5).
 *
 * THROWS when the id is not a resolved positive integer: no store id, no
 * cart request. There is no account-wide cart to fall back to, and the
 * server would refuse the request anyway.
 */
export function cartStoreArgs(storeId) {
  const id = normalizeStoreId(unref(storeId));
  const value = id == null ? NaN : Number(id);
  if (!Number.isInteger(value) || value <= 0) {
    throw new Error("No store is resolved for this cart request.");
  }
  return { store_id: value };
}
